// client/src/services/apiService.js

const BASE_URL = '/api'; // API prefix

/**
 * A helper function to make API requests.
 * @param {string} endpoint - The API endpoint (e.g., '/documents').
 * @param {object} options - Optional fetch options.
 * @returns {Promise<any>} - The JSON response from the API.
 * @throws {Error} - Throws an error if the API request fails.
 */
async function request(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;
  const defaultOptions = {
    // send cookies for same-origin and cross-origin as needed (SameSite=None)
    credentials: 'include',
    headers: {
      'Accept': 'application/json'
    }
  };
  const merged = { ...defaultOptions, ...options, headers: { ...defaultOptions.headers, ...(options.headers || {}) } };
  const response = await fetch(url, merged);

  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    let needsAuth = false;
    let needsPropelAuth = false;

    try {
      // Try to get a more specific error message from the response body
      const errorData = await response.json();

      if (errorData) {
        if (errorData.message || errorData.error) {
          errorMessage = errorData.message || errorData.error;
        }

        // Check if this is an authentication error
        if (errorData.needsAuth === true) {
          needsAuth = true;
        }
        if (errorData.needsPropelAuth === true) {
          needsPropelAuth = true;
        }
      }
    } catch (e) {
      // If response is not JSON or error parsing JSON, stick with the status text
      console.warn('Could not parse error response as JSON:', e);
    }

    // Handle authentication errors (401)
    if (response.status === 401) {
      // Special case: for Propel status checks, do NOT redirect to Onshape login.
      // This avoids an infinite redirect loop when Propel is unauthenticated.
      if (endpoint === '/propel/status' || options.noAuthRedirect) {
        return { authenticated: false };
      }

      // For Propel-specific auth failures, don't redirect to Onshape OAuth.
      // Instead throw a typed error so callers can prompt Propel re-auth.
      const isPropelEndpoint = endpoint.startsWith('/propel/') || endpoint === '/category-mappings';
      if (needsPropelAuth || isPropelEndpoint) {
        const propelError = new Error(errorMessage || 'Propel authentication required.');
        propelError.status = 401;
        propelError.needsPropelAuth = true;
        throw propelError;
      }

      console.warn('Authentication error detected. Redirecting to login...');

      // Guard against redirect loops: if we already tried within the last 10 seconds,
      // do not redirect again — the session is not persisting (cookie/SameSite issue).
      const REDIRECT_KEY = 'onshape_auth_redirect_at';
      const lastRedirect = parseInt(sessionStorage.getItem(REDIRECT_KEY) || '0', 10);
      if (Date.now() - lastRedirect < 10000) {
        console.error('Auth redirect loop detected — session is not persisting.');
        const loopError = new Error('auth_loop');
        loopError.status = 401;
        loopError.authLoop = true;
        throw loopError;
      }
      sessionStorage.setItem(REDIRECT_KEY, String(Date.now()));

      // Always redirect within the current window context (iframe-safe)
      // This prevents breaking out of the Onshape iframe
      window.location.href = '/oauthSignin';

      // Return a specific error that UI can handle
      const authError = new Error('Authentication required. Redirecting to login...');
      authError.status = 401;
      authError.redirecting = true;
      throw authError;
    }
    
    const error = new Error(errorMessage);
    error.status = response.status;
    error.needsAuth = needsAuth;
    throw error;
  }

  // Handle cases like 204 No Content, where response.json() would fail
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

/**
 * Check overall authentication status for both Onshape and Propel.
 * This is used by the React app on initial load.
 * @returns {Promise<{isOnshapeAuthenticated: boolean, isPropelAuthenticated: boolean, user: object|null}>}
 */
export async function checkAuthStatus() {
  try {
    // First, check the current user (Onshape auth)
    let user = null;
    let isOnshapeAuthenticated = false;
    try {
      user = await request('/user');
      isOnshapeAuthenticated = !!user;
    } catch (error) {
      if (error.authLoop) {
        // Redirect loop detected — surface it so App.js can show a helpful error
        return { isOnshapeAuthenticated: false, isPropelAuthenticated: false, user: null, authLoop: true };
      }
      if (error.status === 401) {
        // Not authenticated with Onshape
        isOnshapeAuthenticated = false;
        user = null;
      } else {
        console.error('[apiService.js] Error checking Onshape auth status:', error);
        throw error;
      }
    }

    // Then, check Propel authentication status
    let isPropelAuth = false;
    try {
      const propelStatus = await request('/propel/status', { noAuthRedirect: true });
      isPropelAuth = !!(propelStatus && propelStatus.authenticated);
    } catch (error) {
      console.warn('[apiService.js] Error checking Propel auth status:', error);
      isPropelAuth = false;
    }

    return {
      isOnshapeAuthenticated,
      isPropelAuthenticated: isPropelAuth,
      user
    };
  } catch (error) {
    console.error('[apiService.js] checkAuthStatus failed:', error);
    return {
      isOnshapeAuthenticated: false,
      isPropelAuthenticated: false,
      user: null
    };
  }
}

/**
 * Fetch server-side feature flags
 * @returns {Promise<{enablePushAttachments:boolean, enablePushBOM:boolean}>}
 */
export async function getFeatureFlags() {
  try {
    return await request('/feature-flags');
  } catch (error) {
    console.error('[apiService.js] Error fetching feature flags:', error);
    // Safe default: disable features if flags cannot be fetched
    return { enablePushAttachments: false, enablePushBOM: false };
  }
}

/**
 * Debug endpoint to preview Propel JSON for selected rows
 * @param {Array<{id:string,name:string,category:string,documentId:string,wvmId:string,elementId:string,partId:string}>} rows
 * @returns {Promise<{success:boolean, propelJSON:object}>}
 */
export async function debugPropelJSON(rows) {
  try {
    return await request('/propel/items/debug-json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows })
    });
  } catch (error) {
    console.error('[apiService.js] Error generating debug Propel JSON:', error);
    throw error;
  }
}

/**
 * Debug endpoint to preview BOM Export JSON using the parts already loaded in the browser
 * @param {Array<object>} parts - The frontend parts array (from BOM table state)
 * @returns {Promise<{success:boolean, propelJSON:object}>}
 */
export async function debugBomJSON(parts) {
  try {
    return await request('/propel/bom/debug-json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parts })
    });
  } catch (error) {
    console.error('[apiService.js] Error generating debug BOM JSON:', error);
    throw error;
  }
}

/**
 * Start a background job to create Propel Items for given rows and write back to Onshape
 * @param {Array<{id:string,name:string,category:string,documentId:string,wvmId:string,elementId:string,partId:string}>} rows
 * @returns {Promise<{success:boolean, jobId:string}>}
 */
export async function createItemsBatch(rows, documentName) {
  try {
    return await request('/propel/items/batch-create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows, documentName })
    });
  } catch (error) {
    console.error('[apiService.js] Error starting batch create items job:', error);
    throw error;
  }
}

/**
 * Export BOM from Onshape to Propel
 * Only items with Propel IDs will be included
 * @param {string} documentId - Onshape document ID
 * @param {string} workspaceId - Onshape workspace ID
 * @param {string} elementId - Onshape element ID
 * @returns {Promise<{success:boolean, jobId:string}>}
 */
export async function exportBomToPropel(documentId, workspaceId, elementId, documentName) {
  try {
    return await request('/propel/export-bom', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentId, workspaceId, elementId, documentName })
    });
  } catch (error) {
    console.error('[apiService.js] Error starting BOM export job:', error);
    throw error;
  }
}

/**
 * Commit (save) an already-staged Import Session in Propel
 * @param {string} importSessionId - The Import Session ID to commit
 * @returns {Promise<{success:boolean, status:string, importSessionId:string}>}
 */
export async function commitImportSession(importSessionId) {
  try {
    return await request('/propel/commit-import-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ importSessionId })
    });
  } catch (error) {
    console.error('[apiService.js] Error committing Import Session:', error);
    throw error;
  }
}

/**
 * Export Attachments (STEP/PDF) for selected items
 * @param {string} documentId
 * @param {string} workspaceId
 * @param {string} elementId
 * @param {Array<object>} items - Array of items to export attachments for
 * @param {Array<string>} formats - Array of formats (e.g. ['STEP', 'PDF'])
 * @returns {Promise<{success:boolean, jobId:string}>}
 */
export async function exportAttachments(documentId, workspaceId, elementId, items, formats, documentName) {
  try {
    return await request('/propel/export-attachments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentId, workspaceId, elementId, items, formats, documentName })
    });
  } catch (error) {
    console.error('[apiService.js] Error starting export attachments job:', error);
    throw error;
  }
}



/**
 * Fetches the current authenticated user's information.
 * @returns {Promise<object|null>} - A promise that resolves to the user object or null if not authenticated.
 */
export async function getCurrentUser() {
  console.log('[apiService.js] getCurrentUser called');
  try {
    console.log('[apiService.js] Attempting to request /user');
    const user = await request('/user');
    console.log('[apiService.js] /user request successful, user:', user);
    return user;
  } catch (error) {
    if (error.status === 401) {
      console.log('[apiService.js] /user request returned 401 - not authenticated');
      return null; // Not authenticated
    }
    console.error('[apiService.js] Error in getCurrentUser (not 401):', error);
    throw error; // Re-throw other errors
  }
}


/**
 * Checks if the user is authenticated with Propel.
 * @returns {Promise<boolean>} - A promise that resolves to true if authenticated with Propel, false otherwise.
 */
export const isPropelAuthenticated = async () => {
  try {
    const response = await request('/propel/status');
    console.log('[apiService.isPropelAuthenticated] API response.data:', response);
    return response.authenticated;
  } catch (error) {
    console.error('[apiService.js] Error checking Propel authentication:', error);
    return false;
  }
}

/**
 * Logs out from Propel by clearing the authentication cookie
 * @returns {Promise<boolean>} - A promise that resolves to true if logout was successful
 */
export async function logoutFromPropel() {
  try {
    const response = await fetch('/api/propel/logout', {
      method: 'POST',
      credentials: 'include'
    });
    
    if (response.ok) {
      console.log('[logoutFromPropel] Successfully logged out from Propel');
      return true;
    } else {
      console.error('[logoutFromPropel] Failed to logout:', response.status);
      return false;
    }
  } catch (error) {
    console.error('[logoutFromPropel] Error:', error);
    return false;
  }
}

/**
 * Get available Propel organizations for the current tenant
 * @returns {Promise<Array>} - Array of available Propel orgs
 */
export async function getAvailablePropelOrgs() {
  try {
    // Include companyId from the URL so the server can validate the correct tenant.
    // express.static serves the SPA HTML before session middleware runs, so the
    // companyId never reaches the server via the initial page load — we must send it
    // explicitly with the first API call.
    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get('companyId');
    const url = companyId
      ? `/api/propel-orgs/available?companyId=${encodeURIComponent(companyId)}`
      : '/api/propel-orgs/available';

    const response = await fetch(url, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      console.error('[getAvailablePropelOrgs] Failed to fetch:', response.status);
      return [];
    }
    
    const orgs = await response.json();
    console.log('[getAvailablePropelOrgs] Available orgs:', orgs);
    return orgs;
  } catch (error) {
    console.error('[getAvailablePropelOrgs] Error:', error);
    return [];
  }
}

/**
 * Select a Propel organization
 * @param {number} propelOrgId - ID of the Propel org to select
 * @returns {Promise<boolean>} - True if selection was successful
 */
export async function selectPropelOrg(propelOrgId) {
  try {
    const response = await fetch('/api/propel-orgs/select', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ propelOrgId })
    });
    
    if (!response.ok) {
      console.error('[selectPropelOrg] Failed to select:', response.status);
      return false;
    }
    
    const result = await response.json();
    console.log('[selectPropelOrg] Selected org:', result.selected);
    return true;
  } catch (error) {
    console.error('[selectPropelOrg] Error:', error);
    return false;
  }
}

/**
 * Initiates the Propel OAuth authentication flow using a popup window.
 * This avoids CSP frame-src violations in Onshape by using a separate window for authentication.
 * @param {number} propelOrgId - ID of the Propel org to authenticate with
 * @returns {Promise<boolean>} - A promise that resolves to true if authentication was successful
 */
export async function authenticateWithPropel(propelOrgId) {
  try {
    if (!propelOrgId) {
      console.error('[authenticateWithPropel] Propel org ID is required');
      return false;
    }
    
    // First, get the auth URL from our server using the existing GET route
    const response = await fetch(`/api/propel/oauth/login?popup=true&propelOrgId=${propelOrgId}`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    
    if (!data.authUrl) {
      console.error('[authenticateWithPropel] No auth URL returned from server');
      return false;
    }
    
    // Open the auth URL in a popup window
    const popupWidth = 600;
    const popupHeight = 700;
    const left = (window.innerWidth - popupWidth) / 2;
    const top = (window.innerHeight - popupHeight) / 2;
    
    const popup = window.open(
      data.authUrl,
      'PropelAuth',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
    ); 
    
    if (!popup) {
      alert('Please allow popups for this site to authenticate with Propel');
      return false;
    }
    
    // Poll for authentication completion
    return new Promise((resolve) => {
      // Listen for postMessage from the popup
      const messageListener = (event) => {
        // Validate the message origin — must come from this app (the popup is served by us)
        const expectedOrigin = window.location.origin;
        if (event.origin !== expectedOrigin) {
          console.warn('[authenticateWithPropel] Ignoring message from unexpected origin:', event.origin);
          return;
        }
        // Check if the message is from our popup
        if (event.data && event.data.type === 'PROPEL_AUTH_SUCCESS') {
          console.log('[authenticateWithPropel] Received success message from popup');
          window.removeEventListener('message', messageListener);
          clearInterval(checkPopup);
          
          // Verify authentication status
          isPropelAuthenticated().then(authenticated => {
            if (authenticated) {
              console.log('[authenticateWithPropel] Successfully authenticated with Propel');
            } else {
              console.warn('[authenticateWithPropel] Received success message but authentication check failed');
            }
            resolve(authenticated);
          });
        }
      };
      
      // Add the message listener
      window.addEventListener('message', messageListener);
      
      // Also check for popup closure as a fallback
      const checkPopup = setInterval(() => {
        try {
          // Check if popup is closed
          if (popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener('message', messageListener);
            // Check if we're authenticated by calling the status endpoint
            isPropelAuthenticated().then(authenticated => {
              resolve(authenticated);
            });
          }
        } catch (e) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageListener);
          resolve(false);
        }
      }, 500);
    });
  } catch (error) {
    console.error('[authenticateWithPropel] Error:', error);
    return false;
  }
}


/**
 * Check the status of a job
 * @param {string} jobId - The ID of the job to check
 * @returns {Promise<Object>} - The job status
 */
export async function checkJobStatus(jobId) {
  try {
    const response = await request(`/job/${jobId}`);
    return response;
  } catch (error) {
    console.error('[apiService.js] Error checking job status:', error);
    throw error;
  }
}


/**
 * Fetches Propel instance info (instanceUrl and namespace).
 * @returns {Promise<{propelInstanceUrl: string, propelNamespace: string}>}
 */
export async function getPropelInfo() {
  try {
    const response = await request('/propel/info', {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error('[apiService.js] Error fetching Propel info:', error);
    throw error;
  }
}

/**
 * Fetches parts/BOM rows for a given element (Assembly or Part Studio).
 * @param {string} documentId - The ID of the document.
 * @param {string} workspaceId - The ID of the workspace.
 * @param {string} elementId - The ID of the element.
 * @param {string} [elementType] - Optional element type (ASSEMBLY, PARTSTUDIO) to avoid extra API call.
 * @returns {Promise<Array<object>>} - A promise that resolves to an array of BOMRowDTO objects.
 */
export async function getBOMTable(documentId, workspaceId, elementId, elementType = null) {
  if (!documentId || !workspaceId || !elementId) {
    throw new Error('Document ID, Workspace ID, and Element ID are required to fetch assembly items.');
  }
  console.log(`[apiService.js] getBOMTable called for d: ${documentId}, w: ${workspaceId}, e: ${elementId}, type: ${elementType}`);
  try {
    let url = `/documents/${documentId}/workspaces/${workspaceId}/elements/${elementId}/bom-table`;
    // Pass elementType as query param if available (avoids extra Onshape API call)
    if (elementType) {
      url += `?elementType=${encodeURIComponent(elementType)}`;
    }
    const bomRows = await request(url);
    console.log('[apiService.js] getBOMTable response:', bomRows);
    return bomRows;
  } catch (error) {
    console.error('[apiService.js] Error in getBOMTable:', error);
    throw error;
  }
}


export async function getAssemblyMetadata(documentId, workspaceId, elementId) {
  return request(`/documents/${documentId}/workspaces/${workspaceId}/elements/${elementId}/assembly-metadata`);
}



/**
 * Fetch current Propel/Salesforce user identity
 * @returns {Promise<{userId:string, username:string, displayName:string, organizationId:string, instanceUrl:string}>}
 */
export async function getPropelWhoAmI() {
  try {
    return await request('/propel/whoami');
  } catch (error) {
    console.error('[apiService.js] Error fetching Propel whoami:', error);
    throw error;
  }
}

/**
 * Fetch static category mappings between Onshape and Propel categories
 * @returns {Promise<Array<{onshapeCategory:string, propelCategory:string}>>}
 */
export async function getCategoryMappings() {
  try {
    return await request('/category-mappings', { headers: { 'Cache-Control': 'no-cache' } });
  } catch (error) {
    console.error('[apiService.js] Error fetching category mappings:', error);
    throw error;
  }
}

/**
 * Fetch Propel categories with optional filters.
 * @param {{type?: string, searchTerm?: string}} params
 * @returns {Promise<Array<{id:string,name:string,type:string,level:number,disabled:boolean,icon:string}>>}
 */
export async function getPropelCategories(params = {}) {
  try {
    const query = new URLSearchParams();
    if (params.type) query.set('type', params.type);
    if (params.searchTerm) query.set('searchTerm', params.searchTerm);
    const qs = query.toString();
    const endpoint = `/propel/categories${qs ? `?${qs}` : ''}`;
    return await request(endpoint, { headers: { 'Cache-Control': 'no-cache' } });
  } catch (error) {
    console.error('[apiService.js] Error fetching Propel categories:', error);
    throw error;
  }
}

// Future API functions can be added here, e.g.:
// export async function getPartDetails(documentId, workspaceId, elementId, partId) {
//   return request(`/documents/${documentId}/workspaces/${workspaceId}/elements/${elementId}/parts/${partId}/details`);
// }
